import type { ReactNode } from 'react';
import { Box } from '@mui/material';

import { BreadcrumbsComponent } from 'app/components/BreadcrumbsComponent';
import { PageHeader } from 'app/components/StyledComponents';
import PleaseSelectDealerPlaceholder from 'app/components/ReusableUIComponents/LowLevelComponents/PleaseSelectDealerPlaceholder';

export type DealerRequiredPageShellProps = {
  hasDealer: boolean;
  breadcrumbsList: Array<{
    label: string;
    link?: string;
    isActive?: boolean;
  }>;
  /** Passed to {@link BreadcrumbsComponent} on crumb click (preserves parent nav state). */
  breadcrumbsNavState?: Record<string, unknown>;
  pageTitle: ReactNode;
  /** Rendered under the title only when `hasDealer` is true (e.g. dealer name + id). */
  headerSubtitle?: ReactNode;
  /**
   * Rendered on the right of the subtitle row (same line as `headerSubtitle`),
   * only when `hasDealer` is true. Useful for header badges / quick stats.
   */
  headerSubtitleRight?: ReactNode;
  /** Space above breadcrumbs/title from the top of the shell. Default `48px`. */
  headerSectionMarginTop?: number | string;
  /** Space below the breadcrumb/title block before main content. Default theme spacing 1 (~8px). */
  headerSectionMarginBottom?: number | string;
  /**
   * `centered`: max-width column; main area uses block flow (no flex-grow) so content stays flush under the header.
   * `fluid`: scrollable region for long configure forms.
   */
  contentVariant?: 'centered' | 'fluid';
  /** Max width for centered variant content wrapper. Default `1200`. */
  centeredMaxWidth?: number | string;
  placeholderMinHeight?: number;
  children?: ReactNode;
};

/**
 * Configure-style page shell: breadcrumbs + title. When no dealer is selected,
 * shows {@link PleaseSelectDealerPlaceholder} instead of page content.
 */
export default function DealerRequiredPageShell({
  hasDealer,
  breadcrumbsList,
  breadcrumbsNavState,
  pageTitle,
  headerSubtitle,
  headerSubtitleRight,
  headerSectionMarginTop = '48px',
  headerSectionMarginBottom = 1,
  contentVariant = 'centered',
  centeredMaxWidth = 1200,
  placeholderMinHeight,
  children,
}: DealerRequiredPageShellProps) {
  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        height: 'calc(100vh - 66px)',
        padding: '0 48px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch',
      }}
    >
      <Box
        sx={{
          marginTop: headerSectionMarginTop,
          marginBottom: headerSectionMarginBottom,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <BreadcrumbsComponent
          breadcrumbsList={breadcrumbsList}
          state={breadcrumbsNavState}
        />
        <PageHeader sx={{ marginBottom: '0 !important' }}>{pageTitle}</PageHeader>
        {hasDealer && (headerSubtitle || headerSubtitleRight) ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              width: '100%',
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ minWidth: 0 }}>{headerSubtitle}</Box>
            {headerSubtitleRight ? (
              <Box sx={{ flexShrink: 0 }}>{headerSubtitleRight}</Box>
            ) : null}
          </Box>
        ) : null}
      </Box>

      {!hasDealer ? (
        <Box
          sx={{
            width: '100%',
            pt: 0,
            pb: 4,
            px: 5,
            boxSizing: 'border-box',
            mt: 2,
          }}
        >
          <PleaseSelectDealerPlaceholder minHeight={placeholderMinHeight} />
        </Box>
      ) : contentVariant === 'centered' ? (
        <Box
          sx={{
            width: '100%',
            pt: 0,
            pb: 4,
            px: 5,
            boxSizing: 'border-box',
            mt: 0,
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: centeredMaxWidth,
              mx: 'auto',
            }}
          >
            {children}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            pb: 4,
            width: '100%',
            boxSizing: 'border-box',
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  );
}
